import { useState, useEffect } from "react";
import { navItems, site } from "@/mocks/site-data";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState("inicio");
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const sections = navItems
      .map((n) => document.getElementById(n.id))
      .filter((el): el is HTMLElement => Boolean(el));

    if (sections.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActive(entry.target.id);
          }
        });
      },
      { rootMargin: "-45% 0px -50% 0px", threshold: 0 }
    );

    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const linkClass = (id: string) =>
    `relative whitespace-nowrap text-sm font-medium transition-colors duration-300 ${
      active === id
        ? scrolled
          ? "text-accent-400"
          : "text-accent-200"
        : scrolled
          ? "text-foreground-400 hover:text-foreground-50"
          : "text-foreground-100/70 hover:text-foreground-50"
    }`;

  return (
    <header
      className={`transition-all duration-500 ${
        scrolled
          ? "bg-background-50/90 backdrop-blur-md border-b border-secondary-300 shadow-[0_1px_0_0_rgba(255,255,255,0.04)]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-8xl items-center justify-between gap-4 px-4 md:px-6">
        <a href="#inicio" className="flex items-center gap-3" aria-label="Início">
          <span
            className={`flex h-10 w-10 items-center justify-center rounded-full border border-accent-400/70 transition-colors duration-300 ${
              scrolled ? "bg-accent-400/10" : "bg-background-50/10"
            }`}
          >
            <img
              src="https://public.readdy.ai/ai/img_res/fe4adf6c-5bca-46d7-9c2e-e5888e9d0642.png"
              alt="Logo Dra. Sâmia Albuquerque"
              className="h-6 w-6 object-contain"
            />
          </span>
          <span className="hidden sm:flex flex-col leading-tight">
            <span
              className={`font-heading text-lg font-semibold transition-colors duration-300 ${
                scrolled ? "text-foreground-50" : "text-foreground-50"
              }`}
            >
              Dra. Sâmia Albuquerque
            </span>
            <span
              className={`text-[10px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                scrolled ? "text-foreground-400" : "text-foreground-100/50"
              }`}
            >
              Médica · {site.crm}
            </span>
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-7" aria-label="Navegação principal">
          {navItems.map((item) => (
            <a key={item.id} href={`#${item.id}`} className={linkClass(item.id)}>
              {item.label}
              <span
                className={`absolute -bottom-1 left-0 h-px bg-accent-500 transition-all duration-300 ${
                  active === item.id ? "w-full" : "w-0"
                }`}
              />
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={site.whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden md:inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-accent-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors duration-300 hover:bg-accent-400"
          >
            Agendar avaliação
          </a>

          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? "Fechar menu" : "Abrir menu"}
            aria-expanded={menuOpen}
            className={`lg:hidden flex h-10 w-10 items-center justify-center rounded-md transition-colors duration-300 ${
              scrolled ? "text-foreground-50" : "text-foreground-50"
            }`}
          >
            <i className={`text-2xl ${menuOpen ? "ri-close-line" : "ri-menu-line"}`} />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="max-h-[calc(100vh-108px)] overflow-y-auto border-t border-secondary-300 bg-background-50 lg:hidden">
          <nav className="mx-auto flex max-w-8xl flex-col px-4 py-4 md:px-6" aria-label="Menu móvel">
            {navItems.map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                onClick={() => setMenuOpen(false)}
                className={`flex items-center justify-between border-b border-secondary-300 py-3.5 text-base font-medium transition-colors ${
                  active === item.id ? "text-accent-400" : "text-foreground-300"
                }`}
              >
                {item.label}
                <i className="ri-arrow-right-s-line text-foreground-600" />
              </a>
            ))}
            <a
              href={site.whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-accent-500 px-5 py-3.5 text-base font-semibold text-background-50"
            >
              Agendar avaliação
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}